import { SwageazyInventoryFilters } from "../models/SwageazyInventory/SwageazyInventoryFilter";
import SwageazyInventoryAPI from "../api/SwageazyInventoryAPI";
import {
  SwageazyInventoryDetails,
  SwageazyInventoryInwardRequestFormData,
  SwageazyInventoryOutwardRequestFormData,
  SwageazyInventorySampleReturnRequestFormData,
  PurchaseOrderProduct,
  PurchaseOrderProductDetails,
  BlockedQuantityDetailsModel,
} from "../models/SwageazyInventory/SwageazyInventory";
import { PurchaseRequestProductRequestBody } from "../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { ProductVariantData } from "../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { ProjectSourcingProduct } from "../models/ProjectManagement/ProjectSourcingProduct";
import { action, makeObservable, observable, runInAction } from "mobx";
import { PurchaseRequestBody } from "../models/PurchaseRequest/PurchaseRequestBody";
import { ProductStockDetails } from "../models/SwageazyInventory/SwageazyInventory";
import { PurchaseOrders } from "../models/SwageazyInventory/SwageazyInventory";
import { FilterValue } from "../models/Filters/FilterValue";

export class SwageazyInventoryStore {
  swageazyInventoryAPI: SwageazyInventoryAPI;
  swageazyInventoryListingResponse: SwageazyInventoryDetails[];
  purchaseRequestFormData: PurchaseRequestBody;
  inwardRequestFormData: SwageazyInventoryInwardRequestFormData;
  outwardRequestFormData: SwageazyInventoryOutwardRequestFormData;
  sampleReturnRequestFormData: SwageazyInventorySampleReturnRequestFormData | null;
  productStockDetails: ProductStockDetails | null;
  blockStockDetailsResponce: BlockedQuantityDetailsModel | null;
  limit: number;
  total_count: number;
  total_pages: number;
  filterCount: number;
  isLoading: boolean;
  isFetchingData: boolean;
  selectedProduct: number | null;
  selectedProductImages: string[];
  popupState: {
    openFilter: boolean;
    openImageGallery: boolean;
    openCreatePrPopup: boolean;
    openInwardPopup: boolean;
    openOutwardPopup: boolean;
    openSampleReturnPopup: boolean;
    InventoryStockQuantityDetailsPopup: boolean;
    openvendorPopup: boolean;
  };
  poProducts: PurchaseOrderProduct[];
  poSearchResults: FilterValue[];
  orderProducts: PurchaseOrderProduct[];
  orderSearchResults: FilterValue[];
  products: ProjectSourcingProduct[];
  purchaseRequestProducts: PurchaseRequestProductRequestBody[];
  selectedProductVariants: Map<number, ProductVariantData[]>;
  availableVariantOptions: { [key: string]: FilterValue[] };
  apiTimer: NodeJS.Timeout | null;
  hoverIndex: number | null;
  productSearchText: string;

  constructor() {
    this.swageazyInventoryAPI = new SwageazyInventoryAPI();
    this.swageazyInventoryListingResponse = [];
    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.filterCount = 0;
    this.isLoading = false;
    this.isFetchingData = false;
    this.selectedProduct = null;
    this.selectedProductImages = [];
    this.purchaseRequestFormData = {};
    this.inwardRequestFormData = {};
    this.outwardRequestFormData = {};
    this.sampleReturnRequestFormData = null;
    this.blockStockDetailsResponce = null;
    this.popupState = {
      openFilter: false,
      openImageGallery: false,
      openCreatePrPopup: false,
      openInwardPopup: false,
      openOutwardPopup: false,
      openSampleReturnPopup: false,
      InventoryStockQuantityDetailsPopup: false,
      openvendorPopup: false,
    };
    this.poProducts = [];
    this.poSearchResults = [];
    this.orderProducts = [];
    this.orderSearchResults = [];
    this.products = [];
    this.purchaseRequestProducts = [];
    this.selectedProductVariants = new Map();
    this.availableVariantOptions = {};
    this.productStockDetails = null;
    this.apiTimer = null;
    this.hoverIndex = null;
    this.productSearchText = "";

    makeObservable(this, {
      swageazyInventoryListingResponse: observable,
      limit: observable,
      total_count: observable,
      total_pages: observable,
      filterCount: observable,
      isLoading: observable,
      isFetchingData: observable,
      selectedProduct: observable,
      selectedProductImages: observable,
      products: observable,
      purchaseRequestProducts: observable,
      selectedProductVariants: observable,
      availableVariantOptions: observable,
      productStockDetails: observable,
      blockStockDetailsResponce: observable,
      purchaseRequestFormData: observable,
      inwardRequestFormData: observable,
      outwardRequestFormData: observable,
      sampleReturnRequestFormData: observable,
      poProducts: observable,
      poSearchResults: observable,
      orderProducts: observable,
      orderSearchResults: observable,
      apiTimer: observable,
      popupState: observable,
      hoverIndex: observable,
      productSearchText: observable,
      setProductSearchText: action,
      setIsLoading: action,
      getSwageazyInventoryListing: action,
      managePopup: action,
      setSelectedProductImages: action,
      setPurchaseRequestFormData: action,
      setInwardRequestFormData: action,
      setSampleReturnRequestFormData: action,
      setOutwardRequestFormData: action,
      updateInwardRequestFormData: action,
      resetInwardRequestFormData: action,
      resetSampleReturnRequestFormData: action,
      resetOutwardRequestFormData: action,
      resetCreatePurchaseRequestState: action,
      setPoProducts: action,
      setPoSearchResults: action,
      setOrderProducts: action,
      setOrderSearchResults: action,
      setAvailableVariantOptions: action,
      setApiTimer: action,
      clearApiTimer: action,
      setHoverIndex: action,
      updateInventoryListingAfterOutward: action,
      updateInventoryListingAfterSampleReturn: action,
      downloadSwageazyInventoryReport: action,
    });
  }

  getSwageazyInventoryListing = async (filters: SwageazyInventoryFilters) => {
    try {
      const response =
        await this.swageazyInventoryAPI.getSwageazyInventoryListing(filters);
      this.swageazyInventoryListingResponse =
        response.list as SwageazyInventoryDetails[];
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
    } catch (error) {
      throw error;
    }
  };

  downloadSwageazyInventoryReport = async (
    filters: SwageazyInventoryFilters,
  ) => {
    try {
      const response =
        await this.swageazyInventoryAPI.downloadSwageazyInventoryReport(
          filters,
        );
      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchPurchaseOrderNumbers = async (searchText: string) => {
    try {
      const response =
        await this.swageazyInventoryAPI.getPurchaseOrderNumbers(searchText);
      return response;
    } catch (error) {
      throw error;
    }
  };

  createSwageazyInventoryPurchaseRequest = async () => {
    try {
      const transformedData = {
        ...this.purchaseRequestFormData,
        pr_products: this.purchaseRequestFormData.pr_products?.map(
          (product) => {
            const { id, ...rest } = product;
            return {
              ...rest,
              product_id: id,
            };
          },
        ),
      };
      const response =
        await this.swageazyInventoryAPI.createSwageazyInventoryPurchaseRequest(
          transformedData,
        );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createSwageazyInventoryInwardTransfer = async () => {
    try {
      const { images, ...formData } = this.inwardRequestFormData;

      const payload = {
        ...formData,
        variants: (this.inwardRequestFormData.variants || []).map((variant) => {
          let sku = variant.sku;
          if (
            variant.variant_options &&
            Object.keys(variant.variant_options).length > 0
          ) {
            sku = Object.values(variant.variant_options)
              .filter((v) => v)
              .join("-")
              .toLowerCase();
          }

          const { variant_options, ...variantPayload } = variant;

          if (variantPayload.quantity_rejected === undefined) {
            variantPayload.quantity_rejected = 0;
          }
          if (variantPayload.rejection_reason === undefined) {
            variantPayload.rejection_reason = "";
          }
          if (
            variantPayload.expiry_date === undefined ||
            variantPayload.expiry_date === ""
          ) {
            delete variantPayload.expiry_date;
          }

          return { ...variantPayload, sku };
        }),
      };

      const response =
        await this.swageazyInventoryAPI.createSwageazyInventoryInwardTransfer(
          payload,
        );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createSwageazyInventoryOutwardTransfer = async () => {
    try {
      const response =
        await this.swageazyInventoryAPI.createSwageazyInventoryOutwardTransfer(
          this.outwardRequestFormData,
        );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateInventoryListingAfterOutward = (
    productId: number,
    totalQuantity: number,
    lastUpdatedBy: string,
  ) => {
    runInAction(() => {
      this.swageazyInventoryListingResponse.forEach((item) => {
        if (item.id === productId) {
          item.quantity = item.quantity - totalQuantity;
          item.blocked_quantity = item.blocked_quantity - totalQuantity;
          item.last_updated_by = lastUpdatedBy;
          item.last_updated_at = new Date().toISOString();
        }
      });
    });
  };

  updateInventoryListingAfterSampleReturn = (
    productId: number,
    totalQuantity: number,
    lastUpdatedBy: string,
  ) => {
    runInAction(() => {
      this.swageazyInventoryListingResponse.forEach((item) => {
        if (item.id === productId) {
          item.quantity = item.quantity + totalQuantity;
          item.last_updated_by = lastUpdatedBy;
          item.last_updated_at = new Date().toISOString();
        }
      });
    });
  };

  sampleReturn = async () => {
    try {
      const formData = this
        .sampleReturnRequestFormData as SwageazyInventorySampleReturnRequestFormData;

      const transformedVariants = (formData.variants || []).map((variant) => {
        let sku = variant.sku;

        if (
          variant.variant_options &&
          Object.keys(variant.variant_options).length > 0
        ) {
          sku = Object.values(variant.variant_options)
            .filter((v) => v)
            .join("-")
            .toLowerCase();
        }

        const { variant_options, ...variantPayload } = variant;

        return {
          ...variantPayload,
          sku: sku || variant.sku,
          quantity: variant.quantity,
          quantity_rejected: variant.quantity_rejected || 0,
        };
      });

      const payload: SwageazyInventorySampleReturnRequestFormData = {
        ...formData,
        variants: transformedVariants,
      };

      const response = await this.swageazyInventoryAPI.sampleReturn(payload);
      return response;
    } catch (error) {
      throw error;
    }
  };

  generateBoxId = async (current_rack: string, warehouse_id: string) => {
    try {
      const response = await this.swageazyInventoryAPI.generateBoxId(
        current_rack,
        warehouse_id,
      );
      if (response && response.result) {
        const boxId = response.result.id;
        if (boxId) {
          runInAction(() => {
            this.inwardRequestFormData.box_id = String(boxId);
          });
        }
        return response;
      }
      return null;
    } catch (error) {
      throw error;
    }
  };

  BlockedQuantityDetails = async (
    po_id: number,
    product_id: number,
    warehouse_id: number | string,
  ) => {
    try {
      const response: BlockedQuantityDetailsModel =
        await this.swageazyInventoryAPI.BlockedQuantityDetails(
          po_id,
          product_id,
          warehouse_id,
        );

      runInAction(() => {
        this.blockStockDetailsResponce = response;
      });

      if (response && response.boxes && Array.isArray(response.boxes)) {
        return response.boxes;
      } else {
        return [];
      }
    } catch (error) {
      runInAction(() => {
        this.blockStockDetailsResponce = null;
      });
      throw error;
    }
  };

  getProductStockDetails = async (product_id: number) => {
    try {
      const response =
        await this.swageazyInventoryAPI.getProductStockDetails(product_id);
      if (response) {
        const result = response as ProductStockDetails;
        runInAction(() => {
          this.productStockDetails = {
            total_stock: result.total_stock || [],
            variants_stock: result.variants_stock || [],
            blocked_stock: result.blocked_stock || [],
          };
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  getProductTransactionsReportUrl = (
    productId: number,
    productName: string,
  ): string => {
    const baseUrl = "/v5/inventories/swageazy";
    const path = `/product/${productId}/transactions`;

    const fullUrl = `/report/data?title=${encodeURIComponent(
      productName,
    )}&base_url=${baseUrl}&path=${path}`;

    return fullUrl;
  };

  //from bellow update or clear function

  managePopup = (
    popup: keyof typeof this.popupState,
    action: "open" | "close",
  ) => {
    runInAction(() => {
      if (action === "open") {
        this.popupState[popup] = true;
      } else {
        this.popupState[popup] = false;
      }
    });
  };

  setIsLoading = (isLoading: boolean) => {
    runInAction(() => {
      this.isLoading = isLoading;
    });
  };

  setApiTimer = (timer: NodeJS.Timeout) => {
    runInAction(() => {
      this.apiTimer = timer;
    });
  };

  clearApiTimer = () => {
    if (this.apiTimer) {
      clearTimeout(this.apiTimer);
      this.apiTimer = null;
    }
  };

  setHoverIndex = (index: number | null) => {
    runInAction(() => {
      this.hoverIndex = index;
    });
  };

  setSelectedProductImages = (productId: number, images: string[]) => {
    runInAction(() => {
      this.selectedProduct = productId;
      this.selectedProductImages = images;
    });
  };

  setAvailableVariantOptions = (options: { [key: string]: FilterValue[] }) => {
    runInAction(() => {
      this.availableVariantOptions = options;
    });
  };

  setPoProducts = (products: PurchaseOrderProduct[]) => {
    runInAction(() => {
      this.poProducts = products;
    });
  };

  setPoSearchResults = (results: FilterValue[]) => {
    runInAction(() => {
      this.poSearchResults = results;
    });
  };

  setOrderProducts = (products: PurchaseOrderProduct[]) => {
    runInAction(() => {
      this.orderProducts = products;
    });
  };

  setOrderSearchResults = (results: FilterValue[]) => {
    runInAction(() => {
      this.orderSearchResults = results;
    });
  };

  updateInwardRequestFormData = (
    field: keyof SwageazyInventoryInwardRequestFormData,
    value: string | number | null | string[] | File[] | undefined,
  ) => {
    runInAction(() => {
      if (field && value !== undefined) {
        Object.assign(this.inwardRequestFormData, { [field]: value || null });
      }
    });
  };

  getPurchaseOrderNumbers = async (searchText: string) => {
    try {
      const response = await this.fetchPurchaseOrderNumbers(searchText);
      if (response) {
        let purchaseOrders: PurchaseOrders[] = [];
        const result = response as PurchaseOrderProductDetails;

        if (result.purchase_orders && Array.isArray(result.purchase_orders)) {
          purchaseOrders = result.purchase_orders;
        } else if (Array.isArray(response)) {
          purchaseOrders = response as PurchaseOrders[];
        }

        runInAction(() => {
          this.poSearchResults = purchaseOrders.map((po: PurchaseOrders) => ({
            id: String(po.po_id),
            value: String(po.po_id),
          }));
        });
      }
      return [];
    } catch (error) {
      throw error;
    }
  };

  //set section

  setPurchaseRequestFormData = (data: PurchaseRequestBody) => {
    runInAction(() => {
      this.purchaseRequestFormData = data;
    });
  };

  setInwardRequestFormData = (data: SwageazyInventoryInwardRequestFormData) => {
    runInAction(() => {
      this.inwardRequestFormData = {
        ...this.inwardRequestFormData,
        ...data,
      };
    });
  };

  setSampleReturnRequestFormData = (
    data: Partial<SwageazyInventorySampleReturnRequestFormData>,
  ) => {
    runInAction(() => {
      if (this.sampleReturnRequestFormData === null) {
        this.sampleReturnRequestFormData =
          {} as SwageazyInventorySampleReturnRequestFormData;
      }
      this.sampleReturnRequestFormData = {
        ...this.sampleReturnRequestFormData,
        ...data,
      };
    });
  };

  setOutwardRequestFormData = (
    data: SwageazyInventoryOutwardRequestFormData,
  ) => {
    runInAction(() => {
      this.outwardRequestFormData = {
        ...this.outwardRequestFormData,
        ...data,
      };
    });
  };

  setProductSearchText = (text: string) => {
    runInAction(() => {
      this.productSearchText = text || "";
    });
  };

  //reset section

  resetCreatePurchaseRequestState = () => {
    runInAction(() => {
      this.products = [];
      this.purchaseRequestProducts = [];
      this.selectedProductVariants.clear();
      this.purchaseRequestFormData = {
        emails: [],
      };
    });
  };

  resetSampleReturnRequestFormData = () => {
    runInAction(() => {
      this.sampleReturnRequestFormData = null;
      this.orderProducts = [];
      this.orderSearchResults = [];
    });
  };

  resetInwardRequestFormData = () => {
    runInAction(() => {
      this.inwardRequestFormData = {};
      this.poProducts = [];
      this.poSearchResults = [];
      this.availableVariantOptions = {};
    });
  };

  resetOutwardRequestFormData = () => {
    runInAction(() => {
      this.outwardRequestFormData = {};
      this.poProducts = [];
      this.poSearchResults = [];
    });
  };
}
